// ============================================
// MODALREVINCULAR.JS — ESCANER-VINCULO Fase 2 (mantenimiento)
// ============================================
// Lista los items mal vinculados (tipo beep_fijo) de TODAS las colecciones y permite
// RE-VINCULARLOS a su producto real del catalogo, con confirmacion item por item.
// Corrige costo (real, sin IVA) + modelo + imagen; el PRECIO CONGELADO NO SE TOCA.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import { fetchAuth } from '../utils/sesion';
import { API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE } from './helpersClientes';
import { authHeaders } from '../utils/storage';

const fmtM = (n) => (n == null || !Number.isFinite(Number(n)))
  ? '—'
  : Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ModalRevincular({ onCerrar, onCambio }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [resueltos, setResueltos] = useState({}); // item_id -> { costoAntes, costoDespues }
  const [aplicando, setAplicando] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    fetchAuth(`${API_BASE_CLI}/api/revincular/pendientes`)
      .then(r => r.json())
      .then(d => { if (d.ok) setItems(d.items || []); else setError(d.error || 'Error'); })
      .catch(e => { if (e.code !== 'SESION_EXPIRADA') setError(e.message || 'Error'); })
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const aplicar = async (item, cand) => {
    setAplicando(item.item_id);
    try {
      const r = await fetchAuth(`${API_BASE_CLI}/api/revincular/aplicar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.item_id, fuente: cand.fuente, producto_id: cand.producto_id }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setError(d.error || 'No se pudo re-vincular'); return; }
      setResueltos(prev => ({ ...prev, [item.item_id]: { costoAntes: d.costo_antes, costoDespues: d.costo_despues, margen: d.margen_nuevo } }));
      if (onCambio) onCambio();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError('Error: ' + (e.message || ''));
    } finally {
      setAplicando(null);
    }
  };

  const pendientes = items.filter(it => !resueltos[it.item_id]);

  return (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column', fontFamily: PP, color: COLOR_GRIS,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: COLOR_OSCURO, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <LucideIcons.Link2 size={18} /> Re-vincular productos
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>
            Productos que perdieron su vinculo al catalogo (escaneados como concepto). Elige el producto real:
            se corrige el <strong>costo</strong> y el <strong>modelo</strong>. Tu <strong>precio congelado se mantiene</strong>.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {cargando ? (
            <div style={{ color: COLOR_GRIS_CLARO, padding: 20 }}>Cargando…</div>
          ) : error ? (
            <div style={{ color: '#e05c5c', padding: 12 }}>⚠️ {error}</div>
          ) : items.length === 0 ? (
            <div style={{ color: COLOR_GRIS_CLARO, padding: 30, textAlign: 'center' }}>
              No hay productos pendientes de re-vincular. 🎉
            </div>
          ) : (
            <>
              {pendientes.length === 0 && (
                <div style={{ color: '#1D9E75', padding: 12, fontWeight: 600 }}>✓ Todo re-vinculado en esta sesion.</div>
              )}
              {items.map(it => {
                const done = resueltos[it.item_id];
                return (
                  <div key={it.item_id} style={{
                    border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12, marginBottom: 10,
                    opacity: done ? 0.7 : 1, background: done ? '#f3fcf8' : '#fff',
                  }}>
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>{it.coleccion_nombre}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, lineHeight: 1.3 }}>{it.nombre}</div>
                    <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                      Costo actual: <strong>{fmtM(it.costo_actual)}</strong> · Precio congelado: <strong>{fmtM(it.precio_congelado)}</strong> <span style={{ color: '#1D9E75' }}>(se mantiene)</span>
                    </div>

                    {done ? (
                      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#1D9E75', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <LucideIcons.CheckCircle2 size={16} /> Re-vinculado · costo {fmtM(done.costoAntes)} → {fmtM(done.costoDespues)} · margen {done.margen != null ? `${done.margen}%` : '—'}
                      </div>
                    ) : (
                      <div style={{ marginTop: 10 }}>
                        {it.candidatos && it.candidatos.length > 0 ? (
                          <>
                            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>Sugerencias del catalogo (elige la correcta):</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {it.candidatos.map((c, k) => (
                                <CandidatoFila key={k} cand={c} disabled={aplicando === it.item_id} onElegir={() => aplicar(it, c)} />
                              ))}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Sin sugerencias automaticas.</div>
                        )}
                        <BuscadorManual disabled={aplicando === it.item_id} onElegir={(c) => aplicar(it, c)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${COLOR_BORDE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>{pendientes.length} pendiente{pendientes.length === 1 ? '' : 's'}</span>
          <button onClick={onCerrar} style={{
            background: COLOR_OSCURO, color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
          }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// Una sugerencia/candidato clicable: miniatura + modelo morado + origen + costo.
function CandidatoFila({ cand, disabled, onElegir }) {
  return (
    <button type="button" onClick={onElegir} disabled={disabled} style={{
      display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
      background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, padding: 8,
      cursor: disabled ? 'default' : 'pointer', fontFamily: PP, opacity: disabled ? 0.6 : 1,
    }}>
      <span style={{ width: 46, height: 46, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: '#f3f4f4', border: `1px solid ${COLOR_BORDE}`, padding: 3, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {cand.img_portada
          ? <img src={cand.img_portada} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
          : <LucideIcons.Package size={18} color={COLOR_GRIS_CLARO} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#6d28d9', fontFamily: 'monospace' }}>{cand.modelo || '—'}</span>
          <span style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, padding: '1px 6px', borderRadius: 10,
            background: cand.fuente === 'beep' ? '#e8faf5' : '#eef2fb',
            color: cand.fuente === 'beep' ? '#1D9E75' : COLOR_OSCURO,
          }}>{cand.fuente === 'beep' ? 'Mis Productos' : 'Syscom'}</span>
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(cand.titulo || '').slice(0, 70)}</div>
        <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>Costo {fmtM(cand.costo_sin_iva)} (sin IVA)</div>
      </div>
      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#1D9E75', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <LucideIcons.Check size={15} /> Es este
      </span>
    </button>
  );
}

// Buscador manual de respaldo (reusa /api/tienda/buscar: Syscom + Beep).
function BuscadorManual({ disabled, onElegir }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [busc, setBusc] = useState(false);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    const term = q.trim();
    if (!abierto || term.length < 2) { setRes([]); return undefined; }
    debRef.current = setTimeout(async () => {
      setBusc(true);
      try {
        const r = await fetch(`${API_BASE_CLI}/api/tienda/buscar?limit=10&q=${encodeURIComponent(term)}`, { headers: authHeaders() }).then(x => x.json());
        const prods = (r && r.productos) || [];
        setRes(prods.map(p => ({
          fuente: p.fuente === 'beep' ? 'beep' : 'syscom',
          producto_id: p.referencia_id, modelo: p.modelo, titulo: p.nombre,
          img_portada: p.imagen_url, costo_sin_iva: p.costo_mxn,
        })).filter(p => p.producto_id));
      } catch { /* noop */ } finally { setBusc(false); }
    }, 300);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [q, abierto]);

  if (!abierto) {
    return (
      <button type="button" onClick={() => setAbierto(true)} disabled={disabled} style={{
        marginTop: 8, background: 'transparent', border: 'none', color: COLOR_OSCURO,
        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP, padding: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}><LucideIcons.Search size={13} /> Buscar otro producto…</button>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <input
        autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar en el catalogo (Syscom + Mis Productos)…"
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, fontSize: 13, fontFamily: PP, outline: 'none', marginBottom: 6 }}
      />
      {busc && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>Buscando…</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
        {res.map((c, k) => <CandidatoFila key={k} cand={c} disabled={disabled} onElegir={() => onElegir(c)} />)}
      </div>
    </div>
  );
}
